
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect } from 'react';
import LoadingPage from '@/components/routing/LoadingPage';
import { logger } from '@/utils/logger';

interface RequireAuthProps {
  children: React.ReactNode;
}

const RequireAuth = ({ children }: RequireAuthProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);
  
  // Debug auth state
  logger.info("RequireAuth - Auth State:", { user, loading, path: location.pathname });

  // Once loading is complete or after a timeout, mark the component as ready
  useEffect(() => {
    // Track if component is mounted
    let mounted = true;
    
    // Set up timeout to ensure we don't wait forever
    const timer = setTimeout(() => {
      if (mounted) {
        logger.info("RequireAuth - Auth check timeout completed");
        setIsReady(true);
      }
    }, 1000);
    
    // If loading is complete, mark as ready
    if (!loading && mounted) {
      setIsReady(true);
    }
    
    // Clean up
    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [loading]);

  // Show loading state while checking auth
  if (loading || !isReady) {
    return <LoadingPage />;
  }

  // If not authenticated after check completes, redirect to login
  if (!user) {
    logger.info("RequireAuth: User not authenticated, redirecting to /auth");
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // User is authenticated, render children
  return <>{children}</>;
};

export default RequireAuth;
